/**
 * "내 품목" 목록 화면 — 등록한 호(품목)를 보여주고, 클릭 시 상세로 이동.
 * 비로그인/빈 목록 안내 포함. 별(제거)은 공유 스토어로 즉시 반영.
 */
import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import {
  fetchWatchlist,
  toggleWatch,
  isLoggedIn,
  invalidate,
  markSeen,
} from "./watchlistApi";

function fmtHs4(code) {
  const s = String(code || "");
  if (s.length === 4) return `${s.slice(0, 2)}.${s.slice(2)}`;
  if (s.length === 6) return `${s.slice(0, 4)}.${s.slice(4)}`;
  if (s.length === 10) return `${s.slice(0, 4)}.${s.slice(4, 6)}.${s.slice(6)}`;
  return s;
}
function fmtDate(yyyymmdd) {
  const s = String(yyyymmdd || "");
  return s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6)}` : s;
}

export default function WatchlistView({ onOpen }) {
  const [items, setItems] = useState(null);
  const load = () =>
    fetchWatchlist().then((rows) => {
      setItems(rows);
      // 변동을 화면에 본 것으로 처리(배지 클리어) — 하이라이트는 현재 화면엔 유지
      if (rows.some((r) => r.has_change)) markSeen();
    });
  useEffect(() => {
    load();
  }, []);

  if (!isLoggedIn())
    return (
      <div style={empty}>
        로그인 후 이용할 수 있습니다. 자주 다루는 호를 등록해 두면 빠르게 확인할
        수 있어요.
      </div>
    );
  if (items === null) return <div style={empty}>불러오는 중…</div>;
  if (!items.length)
    return (
      <div style={empty}>
        아직 등록한 품목이 없습니다.
        <br />
        속견표에서 호를 연 뒤 상단 <b>★ 즐겨찾기 추가</b>를 누르면 여기에
        모입니다.
      </div>
    );

  const remove = async (code) => {
    await toggleWatch(code); // 스토어 토글(제거)
    invalidate();
    setItems((prev) => prev.filter((x) => x.code !== code));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {items.map((it) => {
        const bg = it.has_change
          ? "var(--audit-accent-bg)"
          : "var(--audit-bg-elevated)";
        return (
          <div
            key={it.code}
            onClick={() => onOpen?.(it.code)}
            style={{ ...row, background: bg }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--audit-bg-hover)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = bg)}
          >
            <span style={codeCell}>{fmtHs4(it.code)}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={nameCell}>{it.name || `제${it.code}호`}</span>
              {it.has_change && (
                <span style={changeLine}>
                  <span style={changeBadge}>
                    변동 · 적용개시 {fmtDate(it.latest_strt)}
                  </span>
                </span>
              )}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                remove(it.code);
              }}
              title="세번 즐겨찾기에서 제거"
              style={starBtn}
            >
              <Star size={15} fill="currentColor" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

const empty = {
  padding: "22px 16px",
  color: "var(--audit-text-muted)",
  fontSize: 13,
  lineHeight: 1.6,
  border: "1px dashed var(--audit-border)",
  borderRadius: 8,
  background: "var(--audit-bg-subtle)",
};
const row = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "11px 13px",
  border: "1px solid var(--audit-border)",
  borderRadius: 8,
  background: "var(--audit-bg-elevated)",
  cursor: "pointer",
};
const codeCell = {
  flexShrink: 0,
  fontFamily: "JetBrains Mono, monospace",
  fontSize: 13,
  fontWeight: 700,
  color: "var(--audit-accent-dark)",
  minWidth: 64,
};
const nameCell = {
  display: "block",
  fontSize: 13.5,
  color: "var(--audit-text)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const changeLine = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  marginTop: 4,
};
const changeBadge = {
  fontSize: 10.5,
  fontWeight: 700,
  color: "#fff",
  background: "var(--audit-accent)",
  borderRadius: 10,
  padding: "1px 8px",
};
const starBtn = {
  flexShrink: 0,
  border: "none",
  background: "transparent",
  color: "var(--audit-accent)",
  cursor: "pointer",
  padding: 4,
  display: "flex",
  alignItems: "center",
};
